import { z } from "zod";

import { apiClient } from "./api";

export const findingVerdictSchema = z.enum([
  "duplicate",
  "contradiction",
  "complementary",
  "unrelated",
]);
export type FindingVerdict = z.infer<typeof findingVerdictSchema>;

export const findingStatusSchema = z.enum(["open", "not_actionable", "dismissed", "resolved"]);
export type FindingStatus = z.infer<typeof findingStatusSchema>;

export const evidencePassageSchema = z.object({
  item_a_excerpt: z.string(),
  item_b_excerpt: z.string(),
  similarity: z.number(),
});
export type EvidencePassage = z.infer<typeof evidencePassageSchema>;

/** Approval status of a knowledge item: only `approved` (and not archived) is live. */
export const findingItemStatusSchema = z.enum(["approved", "pending", "rejected"]);
export type FindingItemStatus = z.infer<typeof findingItemStatusSchema>;

export const findingItemSchema = z.object({
  id: z.guid(),
  source: z.string(),
  source_ref: z.string().nullable(),
  content_excerpt: z.string(),
  archived_at: z.string().nullable(),
  external_url: z.string().nullable(),
  // Defaulted on read so a stale PWA cache or a lagging deploy never blanks the list.
  status: findingItemStatusSchema.default("approved"),
});
export type FindingItem = z.infer<typeof findingItemSchema>;

export const rejudgedFromStatusSchema = z
  .union([z.literal("dismissed"), z.literal("resolved")])
  .nullable();
export type RejudgedFromStatus = z.infer<typeof rejudgedFromStatusSchema>;

export const findingSchema = z.object({
  id: z.guid(),
  verdict: findingVerdictSchema,
  status: findingStatusSchema,
  similarity: z.number(),
  confidence: z.number(),
  explanation: z.string(),
  evidence: z.array(evidencePassageSchema),
  upstream_changed_at: z.string().nullable(),
  rejudged_from_status: rejudgedFromStatusSchema,
  created_at: z.string(),
  updated_at: z.string(),
  resolved_at: z.string().nullable(),
  // A finding the system closed (an item left the corpus) is resolved with no
  // resolving user and a note; defaulted on read like the item status.
  resolution_note: z.string().nullable().default(null),
  auto_closed: z.boolean().default(false),
  item_a: findingItemSchema,
  item_b: findingItemSchema,
});
export type Finding = z.infer<typeof findingSchema>;

export const findingListSchema = z.object({ items: z.array(findingSchema) });

export const findingCountsSchema = z.object({
  open: z.number().int(),
  dismissed: z.number().int(),
  resolved: z.number().int(),
  not_actionable: z.number().int(),
  all: z.number().int(),
});
export type FindingCounts = z.infer<typeof findingCountsSchema>;

export const scanStatusSchema = z.enum(["queued", "running", "done", "failed"]);
export type ScanStatus = z.infer<typeof scanStatusSchema>;

export const scanSchema = z.object({
  id: z.guid(),
  status: scanStatusSchema,
  pairs_considered: z.number().int(),
  pairs_judged: z.number().int(),
  findings_created: z.number().int(),
  // Judge attempts that raised, and candidate pairs the per-scan cap left unjudged.
  pairs_failed: z.number().int().default(0),
  pairs_remaining: z.number().int().default(0),
  error: z.string().nullable(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
});
export type Scan = z.infer<typeof scanSchema>;

export const scanTriggerSchema = z.object({
  scan_id: z.guid(),
  status: z.string(),
});

export const findingActionSchema = z.object({
  finding_id: z.guid(),
  status: z.string(),
});

export const unarchiveResponseSchema = z.object({
  knowledge_item_id: z.guid(),
  status: z.string(),
});

export const findingOrderBySchema = z.enum(["newest", "oldest", "confidence"]);
export type FindingOrderBy = z.infer<typeof findingOrderBySchema>;

export interface ListFindingsParams {
  status?: FindingStatus | undefined;
  verdict?: FindingVerdict | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
  orderBy?: FindingOrderBy | undefined;
}

export const knowledgeFindingsApi = {
  list: (params: ListFindingsParams = {}) =>
    apiClient.get("/admin/knowledge-findings", findingListSchema, {
      query: {
        status: params.status,
        verdict: params.verdict,
        limit: params.limit,
        offset: params.offset,
        order_by: params.orderBy,
      },
    }),
  counts: (params: { verdict?: FindingVerdict | undefined } = {}) =>
    apiClient.get("/admin/knowledge-findings/counts", findingCountsSchema, {
      query: { verdict: params.verdict },
    }),
  triggerScan: () => apiClient.post("/admin/knowledge-findings/scan", scanTriggerSchema, undefined),
  latestScan: () => apiClient.get("/admin/knowledge-findings/scans/latest", scanSchema.nullable()),
  dismiss: (id: string, note?: string) =>
    apiClient.post(
      `/admin/knowledge-findings/${id}/dismiss`,
      findingActionSchema,
      note ? { note } : undefined,
    ),
  resolve: (id: string, note?: string) =>
    apiClient.post(
      `/admin/knowledge-findings/${id}/resolve`,
      findingActionSchema,
      note ? { note } : undefined,
    ),
  archiveItem: (findingId: string, itemId: string) =>
    apiClient.post(`/admin/knowledge-findings/${findingId}/archive-item`, findingActionSchema, {
      item_id: itemId,
    }),
  unarchiveItem: (itemId: string) =>
    apiClient.post(
      `/admin/knowledge-items/${itemId}/unarchive`,
      unarchiveResponseSchema,
      undefined,
    ),
};

/** An item is live when it is approved and not archived. */
export function itemIsLive(item: FindingItem): boolean {
  return item.status === "approved" && item.archived_at === null;
}

/** A scan is in flight while queued or running — drives polling + button state. */
export function scanInFlight(scan: Scan | null | undefined): boolean {
  return scan?.status === "queued" || scan?.status === "running";
}
